import styled from 'styled-components';
import dateFormat, { masks } from 'dateformat';
import Avatar from '../Avatar';
import { FormTypeEnum } from '../../genericConfigForm';
import { Link } from 'react-router-dom';
import Constants from '../../../constants/constants';
import SplitIOTreatment from '../../../components/splitio/splitioTreatment';
import AuthService from '../../../services/auth/authService';
import mixpanel from 'mixpanel-browser';

import splitsDictionary from '../../../services/utils/splitsDictionary';

const VIDEO_CALL_CLICK_EVENT = {
  eventName: 'Therapy_RestoreSkillsUI_MyPlayers_Click',
  payload: {
    Action: 'VideoCallButton',
    PlayerID: 'PlayerID',
  },
};

const PlayerAvatar = styled(Avatar)`
  margin-right: 1rem;
`;

const Table = styled.table(({ theme }) => {
  const { color, type } = theme;

  return `
    display: grid;
    grid-template-columns: max-content max-content 1fr max-content;

    tr,
    thead,
    tbody {
      display: contents;
    }

    td,
    th {
      display: flex;
      align-items: center;

      &:not(:first-child) {
        padding-left: 1rem;
      }
    }

    th {
      border-bottom: 1px solid ${color.grayscale[800]};
      font-size: ${type.size.small};
      padding-bottom: 15px;

      &:nth-child(3) {
        grid-column: 3 / span 2;
      }
    }

    td {
      padding-top: 24px;
      font-size: ${type.size.small};

      &.noPlayers {
        font-size: ${type.size.large};
        color: ${color.grayscale[0]};
        text-align: center;
        grid-column: 1 / -1;
        display: block;
      }
    }
  `;
});

const TooltipBox = styled.div`
  visibility: hidden;
  color: transparent;
  background-color: transparent;
  width: 350%;
  border-radius: 4px;
  z-index: 999;
  position: relative;
  top: 0px;
  transition: visibility 0.6s, color 0.6s, background-color 0.6s, width 0.3s, padding 0.3s ease-in-out;
`;

const IconContainer = styled.div`
  width: 60px;
  height: 60px;
`;

const linkStyles = (theme, tooltipWidth = null) => {
  const { color } = theme;
  return `
    width: 60px;
    height: 60px;

    text-align: center;
    color: ${color.grayscale[0]};
    transition: 0.2s;

    &:hover {
      font-size:1.2em;
      text-decoration:none;
    }

    ${IconContainer}:hover + ${TooltipBox} {
      visibility: visible;
      color: ${color.grayscale[1000]};
      background-color: ${color.grayscale[0]}80;
      ${tooltipWidth ? 'width: 350%;' : ''}
      padding: 8px 8px;
      border-radius: 4px;
    }
  `;
};

const LinkButton = styled(Link)(({ theme }) => {
  return linkStyles(theme);
});

const RawLink = styled.a(({ theme }) => {
  return linkStyles(theme);
});

const StyledIcon = styled.i(({ theme }) => {
  return `
    position:relative;
    top:25%;
    display: block;

    font-size:2em;
    color: ${theme.color.grayscale[0]};
  `;
});

const EditHomePlayerLink = styled(Link)(({ theme }) => {
  return linkStyles(theme, true);
});

const AddHomePlayerLink = styled(Link)(({ theme }) => {
  return linkStyles(theme, true);
});

const PlayerButton = styled.button(({ theme }) => {
  return linkStyles(theme, true);
});

const StackedIconsContainer = styled.div(() => {
  return `
    position:relative;
    height: 60px;
  `;
});

const HomeI = styled.i(({ theme }) => {
  return `
    position: relative;

    right: 10%;
    top:20%;

    font-size:2.6em;
    color: ${theme.color.grayscale[0]};
  `;
});

const LaptopI = styled.i(({ theme }) => {
  return `
    position: absolute;

    right: 16%;
    top: 50%;

    font-size:1.6em;
    color: ${theme.color.grayscale[0]};

    background-color: ${theme.color.grayscale[900]};
    border-radius: 7px;
  `;
});

const PlayerList = ({ players, openMonitorDialog }) => {
  const playerLoop = players.map((player) => {
    const { id, player_name, home_user_id, last_session, last_activity } = player;

    const parsedLastActivity = !last_activity ? 'N/A' : dateFormat(last_activity, masks.shortDate);

    const parsedLastSession = last_session ? last_session : 'N/A';
    return (
      <tr key={`playerRow-${id}`}>
        {last_activity && (
          <td>
            <PlayerAvatar name={player_name} />
            <a href={`/player/${id}/dashboard`}>{player_name}</a>
          </td>
        )}

        {!last_activity && (
          <td>
            <PlayerAvatar name={player_name} />
            {player_name}
          </td>
        )}

        <td>{parsedLastActivity}</td>

        <td>{parsedLastSession}</td>

        <td>
          {home_user_id && (
            <SplitIOTreatment
              featureName={splitsDictionary.REMOTE_THERAPY.name}
              trafficType={splitsDictionary.REMOTE_THERAPY.traffic_type}
            >
              {(treatment) => {
                if (treatment === 'on') {
                  return (
                    <PlayerButton
                      onClick={() => {
                        openMonitorDialog({ name: player_name, id: id });
                      }}
                    >
                      <IconContainer>
                        <StyledIcon className="fa fa-id-card" aria-hidden="true"></StyledIcon>
                      </IconContainer>
                      <TooltipBox>
                        <p>Monitor Home Play</p>
                      </TooltipBox>
                    </PlayerButton>
                  );
                } else {
                  return null;
                }
              }}
            </SplitIOTreatment>
          )}
          {home_user_id && (
            <EditHomePlayerLink
              to={{
                pathname: `/genericAddForm`,
                state: { data: { type: FormTypeEnum.ADD_HOME_PLAYER, player: player } },
              }}
            >
              <IconContainer>
                <StyledIcon className="fa fa-pencil-square-o" aria-hidden="true"></StyledIcon>
              </IconContainer>
              <TooltipBox>
                <p>Edit Existing Remote Player</p>
              </TooltipBox>
            </EditHomePlayerLink>
          )}

          {!home_user_id && (
            <AddHomePlayerLink
              to={{
                pathname: `/genericAddForm`,
                state: { data: { type: FormTypeEnum.ADD_HOME_PLAYER, player: player } },
              }}
            >
              <IconContainer>
                <StackedIconsContainer>
                  <HomeI className="fa fa-home" aria-hidden="true"></HomeI>
                  <LaptopI className="fa fa-laptop" aria-hidden="true"></LaptopI>
                </StackedIconsContainer>
              </IconContainer>
              <TooltipBox>
                <p>+ Add New Remote Player</p>
              </TooltipBox>
            </AddHomePlayerLink>
          )}

          {last_activity && (
            <LinkButton to={`/player/${id}/dashboard`} aria-label={`View ${player_name}`}>
              <IconContainer>
                <StyledIcon className="fa fa-bar-chart" aria-hidden="true"></StyledIcon>
              </IconContainer>
              <TooltipBox>
                <p>View Player Stats</p>
              </TooltipBox>
            </LinkButton>
          )}
          <SplitIOTreatment featureName="DC-1697-raw-data-download" trafficType="business-group">
            {(treatment) => {
              if (treatment === 'on') {
                if (!last_activity) {
                  return null;
                }
                const token = AuthService.getAuthToken(); //DONE: get token from local storage
                return (
                  <RawLink
                    href={`${Constants.BACKEND_URL}/etl/raw_tracker_data/${id}?token=${token}`}
                    aria-label={`Download Raw Data for ${player_name}`}
                    download
                  >
                    <IconContainer>
                      <StyledIcon className="fa fa-download" aria-hidden="true"></StyledIcon>
                    </IconContainer>
                    <TooltipBox>
                      <p>Download Raw Data</p>
                    </TooltipBox>
                  </RawLink>
                );
              } else {
                return null;
              }
            }}
          </SplitIOTreatment>

          {home_user_id && (
            <SplitIOTreatment
              featureName={splitsDictionary.REMOTE_THERAPY.name}
              trafficType={splitsDictionary.REMOTE_THERAPY.traffic_type}
            >
              {(treatment) => {
                if (treatment === 'on') {
                  return (
                    <LinkButton
                      to={`/remote-therapy-session/${id}`}
                      onClick={() => {
                        mixpanel.track(VIDEO_CALL_CLICK_EVENT.eventName, VIDEO_CALL_CLICK_EVENT.payload);
                      }}
                    >
                      <IconContainer>
                        <StyledIcon className="fa fa-video-camera" aria-hidden="true"></StyledIcon>
                      </IconContainer>
                      <TooltipBox>
                        <p>Join video call</p>
                      </TooltipBox>
                    </LinkButton>
                  );
                } else {
                  return null;
                }
              }}
            </SplitIOTreatment>
          )}
        </td>
      </tr>
    );
  });

  return (
    <Table>
      <thead>
        <tr>
          <th>Player</th>
          <th>Last session</th>
          <th>Last functional skill</th>
        </tr>
      </thead>

      <tbody>
        {players?.length ? (
          playerLoop
        ) : (
          <tr>
            <td className="noPlayers">0 players found.</td>
          </tr>
        )}
      </tbody>
    </Table>
  );
};

export default PlayerList;
